import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, KeyRound, Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { CredentialHealth } from "@/components/credentials/CredentialShell";
import { validationFeedback } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { cn } from "@/lib/utils";

interface AliExpressCredentialSectionProps {
  readonly credential: AffiliateCredentialPublic | undefined;
  readonly loadingCredentials: boolean;
  readonly sectionId?: string | undefined;
}

export function AliExpressCredentialSection({
  credential,
  loadingCredentials,
  sectionId,
}: AliExpressCredentialSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isConfigured = Boolean(credential?.tag_masked || credential?.public_id);

  const handleSaveCredential = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const normalizedAppKey = appKey.trim();
    const normalizedAppSecret = appSecret.trim();
    const normalizedTrackingId = trackingId.trim();

    setStatus(null);
    setError(null);

    if (!normalizedAppKey || !normalizedAppSecret || !normalizedTrackingId) {
      setError("Informe App Key, App Secret e Tracking ID.");
      return;
    }

    setSaving(true);
    const { error: rpcError } = await supabase.rpc("upsert_aliexpress_affiliate_credential", {
      p_app_key: normalizedAppKey,
      p_app_secret: normalizedAppSecret,
      p_tracking_id: normalizedTrackingId,
      p_app_signature: null,
    });
    setSaving(false);

    if (rpcError) {
      setError(aliexpressRpcErrorLabel(rpcError.message));
      return;
    }

    setAppKey("");
    setAppSecret("");
    setTrackingId("");
    const feedback = await validationFeedback("aliexpress");
    setStatus(feedback.status);
    setError(feedback.error);
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
  };

  return (
    <section
      id={sectionId}
      className="overflow-hidden rounded-xl border border-border bg-card"
      aria-labelledby="section-aliexpress"
    >
      <div className="flex items-center justify-between gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <h2
          id="section-aliexpress"
          className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          AliExpress
        </h2>
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
            isConfigured
              ? "border-primary/25 bg-primary/10 text-primary"
              : "border-warning/25 bg-warning/10 text-warning",
          )}
        >
          {isConfigured ? "conectado" : "em implantação"}
        </span>
      </div>

      <div className="space-y-5 p-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Conector API AliExpress Portals</p>
          <p className="text-xs text-muted-foreground">
            O runtime atual gera links pela API oficial usando App Key, App Secret e Tracking ID. A captura
            por extensão segue como evolução de paridade para contas que dependem de sessão.
          </p>
        </div>

        <div className="space-y-2 rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-3">
          <div className="flex items-center gap-2">
            <Link2 size={15} className="text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Identidade da aplicação</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Campos esperados no painel: App Key público, segredo no Vault e Tracking ID.
          </p>
          {!loadingCredentials && isConfigured && credential ? (
            <p className="font-mono text-xs text-foreground break-all">
              Identificador atual: {credential.public_id ?? "Não informado"}
            </p>
          ) : null}
          {loadingCredentials && <p className="text-xs text-muted-foreground">Carregando dados.</p>}
          {!loadingCredentials && !isConfigured && (
            <p className="text-xs text-muted-foreground">
              Nenhuma conexão ativa para o conector principal ainda.
            </p>
          )}
        </div>

        <CredentialHealth credential={credential} />

        <form onSubmit={handleSaveCredential} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="aliexpress-app-key" variant="field">App Key</Label>
            <Input
              id="aliexpress-app-key"
              value={appKey}
              onChange={(event) => setAppKey(event.target.value)}
              placeholder="123456"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="aliexpress-tracking-id" variant="field">Tracking ID</Label>
            <Input
              id="aliexpress-tracking-id"
              value={trackingId}
              onChange={(event) => setTrackingId(event.target.value)}
              placeholder="sua-tracking-id"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="aliexpress-app-secret" variant="field">App Secret</Label>
            <Input
              id="aliexpress-app-secret"
              type="password"
              value={appSecret}
              onChange={(event) => setAppSecret(event.target.value)}
              placeholder="segredo da aplicação"
              autoComplete="off"
            />
          </div>

          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" variant="gold" disabled={saving}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
              Salvar AliExpress
            </Button>
          </div>
        </form>

        {status && (
          <InlineNotice tone="neutral" icon={<CheckCircle2 size={15} />}>
            {status}
          </InlineNotice>
        )}

        {error && (
          <InlineNotice tone="warning" icon={<AlertCircle size={15} />}>
            {error}
          </InlineNotice>
        )}

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Checklist do fluxo atual</p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>App Secret salvo no Vault, sem exposição pela view pública.</li>
            <li>Extensão em desenvolvimento para captura de sessões.</li>
            <li>Importação manual de link oficial permanece como fallback operacional.</li>
          </ul>
        </div>

        <InlineNotice tone="neutral" icon={<CheckCircle2 size={15} />}>
          Quando disponível, a extensão adiciona captura de sessão sem remover o conector API atual.
        </InlineNotice>
      </div>
    </section>
  );
}

function aliexpressRpcErrorLabel(message: string): string {
  if (message.includes("invalid_aliexpress_app_key")) return "App Key inválida.";
  if (message.includes("invalid_aliexpress_app_secret")) return "App Secret inválido.";
  if (message.includes("invalid_aliexpress_tracking_id")) return "Tracking ID inválido.";
  if (message.includes("invalid_aliexpress_app_signature")) return "App Signature inválida.";
  if (message.includes("tenant_not_found")) return "Tenant ativo não encontrado.";
  return "Não foi possível salvar a credencial AliExpress agora.";
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "neutral" | "warning";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "warning"
          ? "border-warning/25 bg-warning/10 text-warning"
          : "border-border bg-[hsl(var(--surface)/0.75)] text-muted-foreground",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}
